import type { QueryClient } from '@tanstack/react-query';
import type { ContributorDto } from '@/types/dtos/contributors/response/contributor.dto';
import { GET_EVENT_CONTRIBUTORS_QUERY_KEY } from '@/state/queries/contributors/use-get-event-contributors';
import { useEventStore } from '@/state/stores/use-event-store';
import { getObjectId } from '@/helpers/object.id.helper';

function patchContributorList(contributors: ContributorDto[] | undefined, updated: ContributorDto) {
  if (!contributors?.some((contributor) => contributor.id === updated.id)) {
    return contributors;
  }
  return contributors.map((contributor) => (contributor.id === updated.id ? updated : contributor));
}

export function resolveContributorEventOriginId(contributor: ContributorDto): string | undefined {
  const event = contributor.event;
  if (!event) {
    return undefined;
  }
  return event.origin ? getObjectId(event.origin) : event.id;
}

export function patchContributorEverywhere(
  queryClient: QueryClient,
  contributor: ContributorDto,
  eventOriginId: string | undefined,
) {
  if (!eventOriginId) {
    return;
  }

  const queryKey = [GET_EVENT_CONTRIBUTORS_QUERY_KEY, eventOriginId] as const;
  const data = queryClient.getQueryData<ContributorDto[]>(queryKey);
  const next = patchContributorList(data, contributor);
  if (next !== data) {
    queryClient.setQueryData(queryKey, next);
  }

  const state = useEventStore.getState();
  const currentEventOriginId = state.event?.origin ? getObjectId(state.event.origin) : state.event?.id;
  if (currentEventOriginId !== eventOriginId) {
    return;
  }
  const contributors = patchContributorList(state.contributors, contributor);
  if (contributors !== state.contributors) {
    useEventStore.setState({ contributors });
  }
}
